import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, MapPin, Calculator, FileText, Phone, AlertCircle, DollarSign, X, ExternalLink, ChevronDown } from 'lucide-react';
import { mockTestimonials, mockFeaturedProperties } from '../data/mockData';
import SocialShareButtons from '../components/SocialShareButtons';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { Helmet } from 'react-helmet-async';
import ParcelMapModal from '../components/ParcelMapModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const homeFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Can I buy a lot in Palm Bay with owner financing?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Owner financing is available on most of our residential lots. No bank qualification required and no prepayment penalty. Full terms, including the APR, are on our price guide, or call for terms."
      }
    },
    {
      "@type": "Question",
      "name": "What is the minimum lot size in Palm Bay?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Standard residential lots in Palm Bay are approximately 10,000 sq ft (quarter acre). Larger parcels and assemblages are also available."
      }
    },
    {
      "@type": "Question",
      "name": "Do you work with national homebuilders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. We regularly work with large builders including DR Horton, Lennar, and Adams Homes, as well as regional and custom builders throughout Brevard County."
      }
    },
    {
      "@type": "Question",
      "name": "Are lots in Palm Bay buildable?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most lots we represent are buildable for single-family residential use. We provide zoning, parcel, and utility information for every listing. For a full parcel and zoning report on any Palm Bay lot, contact us directly."
      }
    },
    {
      "@type": "Question",
      "name": "Do you sell commercial or industrial land?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. In addition to residential lots, we handle commercial, industrial, multifamily, and institutional land throughout Brevard, Polk, Lake, and Marion counties."
      }
    },
    {
      "@type": "Question",
      "name": "How do I get started?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Call or text Vahid directly at 321-333-7230, or submit your lot criteria using the contact form. We'll match you with available inventory that fits your needs."
      }
    }
  ]
};

const Home = () => {
  const { toast } = useToast();
  const [showCashDeals, setShowCashDeals] = useState(false);
  // Cash-only "special" lots, managed in the inventory admin (cashOnly + status).
  const [cashLots, setCashLots] = useState([]);
  const [selectedCashLot, setSelectedCashLot] = useState(null);
  // Client reviews come live from the admin-managed database; fall back to the
  // built-in list only if the database has none yet.
  const [dbReviews, setDbReviews] = useState([]);
  const testimonials = dbReviews.length ? dbReviews : mockTestimonials;
  const reviewCount = testimonials.length;
  // Real average rating (so the summary stays honest when lower-star reviews are shown).
  const avgRating = reviewCount ? (testimonials.reduce((s, t) => s + (Number(t.rating) || 5), 0) / reviewCount) : 5;
  const avgRatingText = avgRating.toFixed(1);
  const avgRounded = Math.round(avgRating);
  // Review popup + FAQ accordion open state
  const [selectedReview, setSelectedReview] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/properties`);
        if (cancelled || !Array.isArray(data)) return;
        const order = { available: 0, under_contract: 1, sold: 2 };
        const lots = data
          .filter((p) => p.cashSpecial)
          .sort((a, b) => (order[a.status || (a.sold ? 'sold' : 'available')] ?? 0) - (order[b.status || (b.sold ? 'sold' : 'available')] ?? 0));
        setCashLots(lots);
      } catch (e) {
        // leave cashLots empty — the modal shows a fallback message
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/reviews`);
        if (!cancelled && Array.isArray(data)) setDbReviews(data);
      } catch (e) { /* fall back to the built-in reviews */ }
    })();
    return () => { cancelled = true; };
  }, []);
  const [heroForm, setHeroForm] = useState({
    name: '',
    phone: '',
    email: '',
    interest: '',
    payment: '',
    details: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const INTEREST_LABELS = {
    sell: 'I want to sell my lot, land, or home',
    lot: 'Looking for a lot',
    'acre-build': 'Looking for a 1+ acre lot to build a home',
    'buy-home': 'Looking to buy a home',
    'build-home': 'Looking to build a home',
    commercial: 'Looking for commercial, industrial, or multifamily',
  };
  const PAYMENT_LABELS = {
    finance: 'Owner financing',
    cash: 'Paying cash',
  };

  const handleHeroFormChange = (e) => {
    setHeroForm({ ...heroForm, [e.target.name]: e.target.value });
  };

  const handleHeroFormSubmit = async (e) => {
    e.preventDefault();
    if (!heroForm.name || !heroForm.phone || !heroForm.email || !heroForm.interest || !heroForm.payment) {
      toast({
        title: "Please fill all fields",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: heroForm.name,
        email: heroForm.email,
        phone: heroForm.phone,
        inquiryType: 'buy',
        message: `${heroForm.details ? `Their question / lot: ${heroForm.details}\n\n` : ''}Interest: ${INTEREST_LABELS[heroForm.interest] || heroForm.interest} | Payment: ${PAYMENT_LABELS[heroForm.payment] || heroForm.payment}\n\nSubmitted from homepage hero form.`,
        smsConsent: true
      });

      toast({
        title: "Request Received!",
        description: "We'll contact you within 24 hours."
      });
      setHeroForm({ name: '', phone: '', email: '', interest: '', payment: '', details: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: "Please call 321-333-7230 directly.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Palm Bay Land for Sale | 500+ Lots | Owner Financing Available</title>
        <meta name="description" content="Residential lots from $41,000 in Palm Bay, FL. Owner financing available, call for terms. Serving buyers, builders, and investors since 2003. 321-333-7230." />
        <meta name="keywords" content="lots for sale in Palm Bay Florida, Palm Bay FL land for sale, owner financing lots Palm Bay, residential lots Brevard County, buy land Palm Bay Florida, Palm Bay lots no HOA, bulk lots for sale Brevard County, commercial land Palm Bay FL, industrial land Palm Bay Florida, land for sale near Space Coast Florida, Palm Bay buildable lots, SW Palm Bay lots for sale, Palm Bay land broker, Brevard County lots owner financing" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://palmbaylots-land.com/" />
        <meta property="og:title" content="Palm Bay Land for Sale | Owner Financing Available" />
        <meta property="og:description" content="Residential lots starting at $41,000 in Palm Bay, FL. Owner financing available. Serving buyers, builders, and investors since 2003." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://palmbaylots-land.com/" />
        <meta property="og:image" content="https://customer-assets.emergentagent.com/job_palmbayhomes/artifacts/am09bmq5_Untitled.png" />
        {/* The business/agent entity is defined once in public/index.html (every page).
            No self-serving review stars here — Google ignores them on a business's own site. */}
        <script type="application/ld+json">{JSON.stringify(homeFaqSchema)}</script>
      </Helmet>
      
      <main id="main-content">
      <div className="min-h-screen">

      {/* Buy-with-confidence bar */}
      <section className="bg-[#1a3a5c] text-white">
        <div className="container mx-auto px-4 py-3 text-center">
          <p className="font-bold text-sm md:text-base">
            🛡️ Buy with confidence — <span className="text-amber-400">9-day cancellation policy</span> &amp; <span className="text-amber-400">same-year lot exchange guarantee</span>*
          </p>
          <p className="text-[11px] text-slate-300 mt-1 max-w-3xl mx-auto leading-snug">
            *No holding costs and no recording fee during the option contract; fees apply after the deed transfers. If you exchange for another lot and prices have gone up, your property is credited at the new price toward the new lot.
          </p>
        </div>
      </section>

      {/* Announcement strips — slim, clickable */}
      <section className="py-2 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {/* Orange — affordability (links to the Palm Bay investment article) */}
            <Link
              to="/blog/palm-bay-13-most-affordable-city-2026"
              className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-lg px-4 py-2.5 text-white flex items-center justify-center gap-2 text-sm md:text-base font-bold hover:opacity-95 transition-opacity"
              data-testid="property-tax-banner"
            >
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>Palm Bay ranked #13 most affordable city in America</span>
              <span className="hidden sm:inline-flex items-center gap-1 underline whitespace-nowrap">See why <ArrowRight className="w-4 h-4" /></span>
            </Link>

            {/* Green — cash deals */}
            <button
              onClick={() => setShowCashDeals(true)}
              className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg px-4 py-2.5 text-white flex items-center justify-center gap-2 text-sm md:text-base font-bold hover:opacity-95 transition-opacity"
              data-testid="cash-deals-banner"
            >
              <DollarSign className="w-4 h-4 flex-shrink-0" />
              <span>Cash buyer discounts on select lots</span>
              <span className="hidden sm:inline-flex items-center gap-1 underline whitespace-nowrap">See deals <ArrowRight className="w-4 h-4" /></span>
            </button>
          </div>
        </div>
      </section>

      {/* Cash Deals Modal */}
      {showCashDeals && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowCashDeals(false)}>
          <div 
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              onClick={() => setShowCashDeals(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              data-testid="close-cash-deals-modal"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-green-100 rounded-full mb-4">
                <DollarSign className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900">Cash Buyer Specials</h2>
              <p className="text-slate-600 mt-2">These lots are priced to sell fast. Cash buyers only — no financing on these deals.</p>
            </div>
            
            <div className="space-y-3 max-h-[50vh] overflow-y-auto">
              {cashLots.length === 0 && (
                <p className="text-center text-slate-500 text-sm py-4">Cash specials are updated regularly — call for current cash pricing.</p>
              )}
              {cashLots.map((lot) => {
                const st = lot.status || (lot.sold ? 'sold' : 'available');
                const addr = [lot.streetNumber, lot.streetName].filter(Boolean).join(' ').trim()
                  || (lot.address || '').trim()
                  || `Unit ${lot.unit || '—'} · Block ${lot.block || '—'} · Lot ${lot.lot || '—'}`;
                const rawPrice = String(lot.price ?? '').trim();
                const price = rawPrice ? (rawPrice.startsWith('$') ? rawPrice : `$${rawPrice}`) : 'Call for price';
                return (
                  <button
                    key={lot.id}
                    type="button"
                    onClick={() => setSelectedCashLot(lot)}
                    className={`w-full flex items-center justify-between gap-3 p-4 rounded-lg border transition-all group text-left ${
                      st === 'sold' ? 'bg-slate-100 border-slate-200 opacity-70'
                      : st === 'under_contract' ? 'bg-amber-50 border-amber-200'
                      : 'bg-slate-50 hover:bg-green-50 border-slate-200 hover:border-green-300'
                    }`}
                    data-testid={`cash-deal-${lot.id}`}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <MapPin className={`w-5 h-5 flex-shrink-0 ${st === 'sold' ? 'text-slate-400' : st === 'under_contract' ? 'text-amber-500' : 'text-green-600'}`} />
                      <span className="text-slate-800 font-medium text-sm truncate">{addr}</span>
                      {st === 'under_contract' && <span className="px-2 py-0.5 bg-amber-500 text-white text-[11px] font-bold rounded uppercase flex-shrink-0">Pending</span>}
                      {st === 'sold' && <span className="px-2 py-0.5 bg-red-600 text-white text-[11px] font-bold rounded uppercase flex-shrink-0">Sold</span>}
                    </span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-green-700 font-bold text-sm">{price}</span>
                      <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-green-600" />
                    </span>
                  </button>
                );
              })}
            </div>
            
            <div className="mt-6 pt-4 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-500 mb-3">Interested? Call for cash pricing:</p>
              <a 
                href="tel:3213337230" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold transition-colors"
              >
                <Phone className="w-5 h-5" />
                321-333-7230
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Property card popup for a clicked cash lot: red-outline satellite + 3 links */}
      {selectedCashLot && (
        <ParcelMapModal item={selectedCashLot} onClose={() => setSelectedCashLot(null)} />
      )}
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-20 overflow-hidden">
        {/* Background Image with lighter overlay so the picture is clearly visible */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?auto=format&fit=crop&w=1920&q=75"
            srcSet="https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?auto=format&fit=crop&w=768&q=70 768w, https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?auto=format&fit=crop&w=1280&q=75 1280w, https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?auto=format&fit=crop&w=1920&q=75 1920w"
            sizes="100vw"
            alt="Florida home with palm trees and green lawn in Palm Bay"
            className="w-full h-full object-cover"
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          {/* Softer left-to-right gradient — readable text on left, visible image on right */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 via-slate-900/55 to-slate-900/15"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent"></div>
        </div>
        
        {/* Content */}
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Left - Headline & CTA */}
            <div className="text-center lg:text-left">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                Own Land in Palm Bay, Florida — <span className="text-amber-400">Residential Lots Starting at $41,000</span> with Owner Financing
              </h1>
              <p className="text-lg text-gray-200 mb-6">
                No bank needed. Cash or owner financing — just fill out a simple application and we handle the rest. 500+ lots available across residential, commercial, and industrial. 23 years of local expertise serving individuals, builders, and investors.
              </p>
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 mb-6">
                <Link 
                  to="/contact" 
                  className="px-8 py-4 bg-amber-500 text-white rounded-lg text-lg font-bold hover:bg-amber-600 transition-colors shadow-xl"
                >
                  Get a Free Land Value Assessment
                </Link>
                <Link
                  to="/inventory"
                  className="px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:border-amber-400 text-white rounded-lg text-lg font-bold hover:bg-white/20 transition-all"
                  data-testid="hero-inventory-link"
                >
                  Browse Our Palm Bay Lot Inventory
                </Link>
              </div>
              <button
                onClick={() => document.getElementById('government-links')?.scrollIntoView({ behavior: 'smooth' })}
                className="mb-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm bg-white/10 border border-white/30 hover:border-amber-400 hover:bg-white/20 text-white rounded-lg font-semibold transition-all"
              >
                🏛 Government &amp; Map Links
              </button>
              <div className="flex flex-col sm:flex-row items-center lg:items-start gap-4 text-gray-200">
                <a href="tel:3213337230" className="flex items-center gap-2 hover:text-amber-400 transition-colors">
                  <Phone className="w-5 h-5" />
                  <span className="font-medium">321-333-7230</span>
                </a>
                <span className="hidden sm:block text-gray-500">|</span>
                <span className="text-amber-400 font-semibold">Thousands of lots sold since 2003</span>
              </div>
            </div>
            
            {/* Right - Lead Capture Form */}
            <div className="bg-white rounded-xl shadow-2xl p-6 md:p-8 max-w-md mx-auto lg:mx-0 lg:ml-auto">
              <h3 className="text-xl font-bold text-slate-900 mb-2 text-center">Get Your Free Lot Info</h3>
              <p className="text-slate-600 text-sm mb-5 text-center">We'll send you available lots and pricing</p>
              
              <form onSubmit={handleHeroFormSubmit} className="space-y-4">
                <div>
                  <Input
                    name="name"
                    value={heroForm.name}
                    onChange={handleHeroFormChange}
                    placeholder="Your Name"
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <Input
                    name="phone"
                    type="tel"
                    value={heroForm.phone}
                    onChange={handleHeroFormChange}
                    placeholder="Phone Number"
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <Input
                    name="email"
                    type="email"
                    value={heroForm.email}
                    onChange={handleHeroFormChange}
                    placeholder="Email Address"
                    required
                    className="w-full"
                  />
                </div>
                <div>
                  <select
                    name="interest"
                    value={heroForm.interest}
                    onChange={handleHeroFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">What are you looking for?</option>
                    <option value="sell">I want to sell my lot, land, or home</option>
                    <option value="lot">Looking for a lot</option>
                    <option value="acre-build">Looking for a 1+ acre lot to build a home</option>
                    <option value="buy-home">Looking to buy a home</option>
                    <option value="build-home">Looking to build a home</option>
                    <option value="commercial">Commercial, industrial, or multifamily</option>
                  </select>
                </div>
                <div>
                  <select
                    name="payment"
                    value={heroForm.payment}
                    onChange={handleHeroFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">Financing or cash?</option>
                    <option value="finance">Owner financing</option>
                    <option value="cash">Paying cash</option>
                  </select>
                </div>
                <div>
                  <textarea
                    name="details"
                    value={heroForm.details}
                    onChange={handleHeroFormChange}
                    placeholder="Which lot or question? (address, unit/block/lot, or what you need)"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white py-3 text-lg font-bold"
                >
                  {isSubmitting ? 'Sending...' : 'Send Me Lot Info'}
                </Button>
                <p className="text-xs text-slate-500 text-center">
                  No obligation. Response within 24 hours.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* 1039 Hooper Ave NE featured listing moved to the Inventory page */}

      {/* Owner Financing Terms */}
      <section className="py-14 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center mb-8">
            <h2 className="text-3xl font-bold text-white mb-3">Owner Financing — No Bank Required</h2>
            <p className="text-slate-400">Easy financing. Clear terms. No prepayment penalty. Banks typically don't lend on vacant land — we do.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-4 mb-6">
            {[
              { val: '30%', lbl: 'Initial Option Payment' },
              { val: '10% / 12.33%–12.58% APR*', lbl: 'Fixed Rate / APR' },
              { val: '10 Years', lbl: 'Financing Term' },
              { val: '40%', lbl: 'Deed Transfer Threshold' },
              { val: '$0', lbl: 'Prepayment Penalty' },
            ].map((item) => (
              <div key={item.lbl} className="bg-slate-800 border border-amber-500 rounded-xl px-6 py-4 text-center min-w-[100px]">
                <p className="text-amber-400 text-2xl font-bold">{item.val}</p>
                <p className="text-slate-400 text-xs mt-1">{item.lbl}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-slate-400 text-sm mb-6">*APR is 12.33% if the 10% financing charge is fully financed, and up to 12.58% if it is paid in full at closing. Example: $25,000 lot, $7,500 (30%) down, $17,500 financed at 10% for 120 months.</p>
          <div className="text-center space-y-4">
            <Link to="/owner-financing-land-florida#financing-disclosure" className="inline-block px-8 py-4 bg-amber-500 text-white rounded-lg font-bold hover:bg-amber-600 transition-colors">
              VIEW FULL FINANCING TERMS
            </Link>
            <div className="pt-2">
              <Link to="/owner-financing-land-florida" className="inline-block text-amber-300 hover:text-amber-200 transition-colors text-sm">
                See lots with owner financing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works - 3 Steps */}
      <section className="py-14 bg-slate-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 text-center mb-10">How it works — 3 simple steps</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                num: '1',
                title: 'Pick your lot',
                body: 'Browse 500+ residential, commercial, and industrial lots. Filter by price, location, or zoning.'
              },
              {
                num: '2',
                title: 'Fill out an application',
                body: 'Cash or owner financing — no bank loan needed. Banks typically don\'t finance vacant land. We make it simple.'
              },
              {
                num: '3',
                title: 'Close and own it',
                body: 'We handle all the paperwork. Close in as little as 2 weeks. Your lot, your land.'
              }
            ].map((step) => (
              <div key={step.num} className="bg-white rounded-xl shadow-md p-6 text-center">
                <div className="w-12 h-12 bg-amber-500 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">{step.num}</div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Palm Bay Market Overview */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Palm Bay, Florida — One of the Southeast's Best Land Markets</h2>
            <p className="text-lg text-slate-600 mb-4">
              <a href="https://www.google.com/maps/place/Palm+Bay,+FL" target="_blank" rel="noopener noreferrer" className="font-semibold underline decoration-amber-500 underline-offset-2 hover:text-amber-700">Palm Bay</a> sits on Florida's Space Coast in East Central Florida — roughly halfway between Miami and Jacksonville and just an hour east of Orlando. That puts it close to Florida's major cities while staying remarkably affordable: in 2026 it was named the <span className="font-semibold text-slate-900">#13 most affordable city in America</span> — the only Florida city in the top 20.
            </p>
            <p className="text-lg text-slate-600">
              It's also <a href="https://www.bcpao.us/" target="_blank" rel="noopener noreferrer" className="underline decoration-amber-500 underline-offset-2 hover:text-amber-700">Brevard County's</a> largest city, with over 130,000 residents and rapid growth fueled by <a href="https://www.spacecoastedc.org/" target="_blank" rel="noopener noreferrer" className="underline decoration-amber-500 underline-offset-2 hover:text-amber-700">Space Coast employers</a> including L3Harris Technologies, Blue Origin, and NASA's Kennedy Space Center. As development pushes south and west, land values continue to rise — making now an ideal time to buy.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center max-w-4xl mx-auto">
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="font-bold text-slate-900">#13</p>
              <p className="text-sm text-slate-600">Most Affordable U.S. City (2026)</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="font-bold text-slate-900">No State Income Tax</p>
              <p className="text-sm text-slate-600">Florida advantage</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="font-bold text-slate-900">1 Hour</p>
              <p className="text-sm text-slate-600">East of Orlando</p>
            </div>
            <div className="bg-slate-50 p-4 rounded-xl">
              <p className="font-bold text-slate-900">I-95 &amp; US-1</p>
              <p className="text-sm text-slate-600">Direct access</p>
            </div>
          </div>
        </div>
      </section>

      {/* Lots & Land for Every Buyer & Purpose (merged) */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Lots &amp; Land for Every Buyer</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">From families buying a single lot to builders and investors acquiring packages of 300+ — with owner financing on most residential lots.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-amber-500">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Residential Lots</h3>
              <p className="text-slate-600 text-sm">Single-family buildable lots throughout Palm Bay and Brevard County. Many with owner financing available.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Commercial Land</h3>
              <p className="text-slate-600 text-sm">Strategically located parcels zoned for retail, office, and mixed use.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-slate-500">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Industrial Land</h3>
              <p className="text-slate-600 text-sm">IU-zoned parcels near major transportation corridors.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Multifamily Parcels</h3>
              <p className="text-slate-600 text-sm">Zoned for duplexes, townhomes, and apartment development.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-500">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Bulk Lot Packages</h3>
              <p className="text-slate-600 text-sm">Assemblages of 10 to 300+ lots for builders and institutional buyers.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
              <h3 className="text-lg font-bold text-slate-900 mb-2">Owner Financing</h3>
              <p className="text-slate-600 text-sm">Flexible owner financing on most residential lots — no traditional bank approval needed. No prepayment penalty.</p>
            </div>
          </div>
        </div>
      </section>

      {/* About Vahid with Photo */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="md:w-1/3">
                <img 
                  src="https://customer-assets.emergentagent.com/job_palmbayhomes/artifacts/am09bmq5_Untitled.png" 
                  alt="Vahid Reza Rajabian - Palm Bay Real Estate Broker Associate"
                  className="w-full rounded-lg shadow-lg"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="md:w-2/3">
                <h2 className="text-3xl font-bold text-slate-900 mb-2">20+ Years in the Palm Bay Land Market</h2>
                <h3 className="text-xl text-slate-600 mb-4">Vahid Rajabian — Broker Associate at M. David Moallem, Inc.</h3>
                <p className="text-amber-600 font-semibold mb-4">Thousands of transactions since 2003 — from single lots to bulk packages for national builders and institutional investors</p>
                <p className="text-slate-700 mb-3">
                  Vahid Rajabian is a licensed Florida Real Estate Broker Associate with over two decades of experience in Palm Bay lots and land. Since 2003, he has facilitated thousands of transactions — from single lots sold to first-time buyers to bulk packages acquired by national builders and institutional investors.
                </p>
                <div className="mb-4">
                  <p className="font-semibold text-slate-900 mb-2">Specialties:</p>
                  <div className="grid grid-cols-2 gap-1 text-sm text-slate-700">
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Residential lots in Brevard County</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Commercial, industrial & multifamily</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Bulk lot assemblages for builders</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Owner-financed land sales</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> Off-market & direct transactions</span>
                    <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> M. David Moallem, Inc. — 35+ years</span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-6 mt-2">
                  <img
                    src="/images/brag-seven-star-2026.png"
                    alt="Broker Agent Advisor (BRAG) 2026 Seven Star Certified badge — Vahid Rajabian"
                    className="h-28 w-auto shrink-0"
                    loading="lazy"
                  />
                  <div className="flex flex-col items-start gap-3">
                    <Link
                      to="/about"
                      className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white rounded-lg font-semibold hover:bg-amber-700 transition-colors"
                    >
                      Learn More About Me
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    {/* "Add as a preferred source on Google" badge */}
                    <div className="inline-block">
                  <a
                    href="https://www.google.com/preferences/source?q=https://palmbaylots-land.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Add palmbaylots-land.com as a preferred source on Google"
                    className="inline-block transition-opacity hover:opacity-90 focus:opacity-90"
                    data-testid="google-preferred-source-badge"
                  >
                    <svg
                      width="243"
                      height="63"
                      viewBox="0 0 243 63"
                      xmlns="http://www.w3.org/2000/svg"
                      role="img"
                      aria-hidden="true"
                    >
                      <rect x="0" y="0" width="243" height="63" rx="10.8" ry="10.8" fill="#000000" stroke="#333" strokeWidth="1" />
                      <g transform="translate(13.5, 15.3) scale(1.35)">
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                          <path d="M1 1h22v22H1z" fill="none" />
                        </svg>
                      </g>
                      <g style={{ fontFamily: 'Tahoma, sans-serif', fontSize: '16.2px', fontWeight: 'bold', fontStyle: 'italic', fill: '#FFFFFF' }}>
                        <text x="58.5" y="28.8">Add as a preferred</text>
                        <text x="58.5" y="48.6">source on Google</text>
                      </g>
                    </svg>
                  </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Properties */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center mb-12">
            <div>
              <h2 className="text-4xl font-bold text-slate-900 mb-2">Featured Properties</h2>
              <p className="text-lg text-slate-600">Explore our current available lots and properties</p>
            </div>
            <Link 
              to="/price-guide" 
              className="hidden md:flex items-center gap-2 text-amber-600 font-semibold hover:text-amber-700 transition-colors"
            >
              View All
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {mockFeaturedProperties.map((property, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow overflow-hidden">
                <div className="h-48 overflow-hidden grid grid-cols-2">
                  <img 
                    src={property.houseImage} 
                    alt={`${property.name} - House Example`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                  <img 
                    src={property.lotImage} 
                    alt={`${property.name} - Lot`}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{property.name}</h3>
                  <p className="text-2xl font-bold text-amber-600 mb-4">{property.price}</p>
                  <div className="space-y-2 mb-4">
                    {property.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/price-guide" 
                    className="block w-full py-2 text-center bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center md:hidden">
            <Link 
              to="/price-guide" 
              className="inline-flex items-center gap-2 text-amber-600 font-semibold hover:text-amber-700 transition-colors"
            >
              View All Properties
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Where We Operate */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Where We Operate</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <p className="font-bold text-slate-900">Palm Bay, FL</p>
              <p className="text-xs text-amber-600">Primary Market</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <p className="font-bold text-slate-900">Brevard County</p>
              <p className="text-xs text-slate-500">FL</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <p className="font-bold text-slate-900">Polk County</p>
              <p className="text-xs text-slate-500">FL</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <p className="font-bold text-slate-900">Lake County</p>
              <p className="text-xs text-slate-500">FL</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <p className="font-bold text-slate-900">Marion County</p>
              <p className="text-xs text-slate-500">FL</p>
            </div>
            <div className="bg-white p-4 rounded-lg shadow-sm text-center">
              <p className="font-bold text-slate-900">Out-of-State</p>
              <p className="text-xs text-slate-500">Commercial</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">What Clients Say</h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className={`w-6 h-6 ${i < avgRounded ? 'text-amber-500' : 'text-slate-300'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
              <span className="text-slate-600 font-medium ml-1">{avgRatingText} — {reviewCount} Verified Reviews</span>
            </div>
            <a 
              href="https://www.ratemyagent.com/real-estate-agent/vahid-rajabian-b14sqj/sales/overview"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-amber-600 hover:text-amber-700 font-medium"
            >
              View all reviews on RateMyAgent
            </a>
          </div>
          <div className="max-w-5xl mx-auto">
            <style>{`
              @keyframes reviewsScrollRTL { from { transform: translateX(0); } to { transform: translateX(-50%); } }
              .reviews-marquee { animation: reviewsScrollRTL 45s linear infinite; }
              .reviews-marquee-wrap:hover .reviews-marquee { animation-play-state: paused; }
              @media (prefers-reduced-motion: reduce) { .reviews-marquee { animation: none; } }
            `}</style>
            <div
              className="reviews-marquee-wrap overflow-hidden"
              data-testid="reviews-scroll"
              style={{ WebkitMaskImage: 'linear-gradient(to right, transparent, #000 4%, #000 96%, transparent)', maskImage: 'linear-gradient(to right, transparent, #000 4%, #000 96%, transparent)' }}
            >
              <div className="reviews-marquee flex gap-4 w-max py-1" style={{ animationDuration: `${Math.max(60, testimonials.length * 8)}s` }}>
                {[...testimonials, ...testimonials].map((testimonial, index) => (
                  <button
                    key={(testimonial.id || 'r') + '-' + index}
                    type="button"
                    onClick={() => setSelectedReview(testimonial)}
                    className="shrink-0 w-[420px] text-left bg-slate-50 p-6 rounded-xl shadow-sm hover:shadow-md hover:bg-white border border-transparent hover:border-amber-300 transition-all"
                    data-testid={`review-card-${index}`}
                  >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className={`w-4 h-4 ${i < (Number(testimonial.rating) || 5) ? 'text-amber-500' : 'text-slate-300'}`} fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    {testimonial.source && (
                      <span className="text-xs text-slate-400">{testimonial.source}</span>
                    )}
                  </div>
                  <p className="text-slate-700 italic line-clamp-4">"{testimonial.text}"</p>
                  <div className="flex items-center justify-between mt-3">
                    <div>
                      <p className="font-semibold text-slate-900">
                        {(testimonial.name && testimonial.name.trim() && testimonial.name.trim().toLowerCase() !== 'verified client') ? (
                          <>{testimonial.name}<span className="ml-1.5 text-xs font-medium text-green-600">· Verified Client</span></>
                        ) : 'Verified Client'}
                      </p>
                      <p className="text-sm text-slate-600">{testimonial.title}{testimonial.date ? ` · ${testimonial.date}` : ''}</p>
                    </div>
                    <span className="text-xs font-medium text-amber-600 whitespace-nowrap ml-3">Read full →</span>
                  </div>
                  {testimonial.reply && testimonial.reply.trim() && (
                    <div className="mt-3 pl-3 border-l-2 border-amber-400 bg-amber-50 rounded-r p-2">
                      <p className="text-xs font-semibold text-amber-700 mb-0.5 flex items-center gap-1"><span>💬</span> Response from Vahid</p>
                      <p className="text-sm text-slate-600 line-clamp-3">{testimonial.reply}</p>
                    </div>
                  )}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-center text-xs text-slate-400 mt-3">Hover to pause · click any review to read it in full</p>
          </div>
        </div>
      </section>

      {/* Review popup */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSelectedReview(null)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6 relative animate-in fade-in zoom-in duration-200" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelectedReview(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors" data-testid="close-review-modal" aria-label="Close review">
              <X className="w-6 h-6" />
            </button>
            <div className="flex items-center gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className={`w-5 h-5 ${i < (Number(selectedReview.rating) || 5) ? 'text-amber-500' : 'text-slate-300'}`} fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p className="text-slate-700 italic mb-4 leading-relaxed">"{selectedReview.text}"</p>
            <p className="font-semibold text-slate-900">
              {(selectedReview.name && selectedReview.name.trim() && selectedReview.name.trim().toLowerCase() !== 'verified client') ? (
                <><span>{selectedReview.name}</span><span className="ml-1.5 text-xs font-medium text-green-600">· Verified Client</span></>
              ) : 'Verified Client'}
            </p>
            <p className="text-sm text-slate-600">{selectedReview.title}{selectedReview.date ? ` · ${selectedReview.date}` : ''}</p>
            {selectedReview.source && <p className="text-xs text-slate-400 mt-1">via {selectedReview.source}</p>}
            {selectedReview.reply && selectedReview.reply.trim() && (
              <div className="mt-4 pl-4 border-l-2 border-amber-400 bg-amber-50 rounded-r-lg p-3">
                <p className="text-xs font-semibold text-amber-700 mb-1">Response from Vahid</p>
                <p className="text-sm text-slate-700">{selectedReview.reply}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* FAQ Section — accordion */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {[
                { q: 'Can I buy a lot in Palm Bay with owner financing?', a: (<>Yes. Owner financing is available on most of our residential lots. No bank qualification required and no prepayment penalty. Full terms, including the APR, are on our price guide, or call for terms.</>) },
                { q: 'What is the minimum lot size in Palm Bay?', a: (<>Standard residential lots in Palm Bay are approximately 10,000 sq ft (quarter acre). Larger parcels and assemblages are also available.</>) },
                { q: 'Do you work with national homebuilders?', a: (<>Yes. We regularly work with large builders including DR Horton, Lennar, and Adams Homes, as well as regional and custom builders throughout Brevard County.</>) },
                { q: 'Are lots in Palm Bay buildable?', a: (<>Most lots we represent are buildable for single-family residential use. We provide zoning, parcel, and utility information for every listing. For a full parcel and zoning report on any Palm Bay lot, <Link to="/contact" className="text-amber-600 hover:underline">contact us directly</Link>.</>) },
                { q: 'Do you sell commercial or industrial land?', a: (<>Yes. In addition to residential lots, we handle commercial, industrial, multifamily, and institutional land throughout Brevard, Polk, Lake, and Marion counties.</>) },
                { q: 'How do I get started?', a: (<>Call or text Vahid directly at <a href="tel:3213337230" className="text-amber-600 hover:underline font-medium">321-333-7230</a>, or <Link to="/contact" className="text-amber-600 hover:underline font-medium">submit your lot criteria</Link> using the contact form. We'll match you with available inventory that fits your needs.</>) },
              ].map((faq, i) => {
                const open = openFaq === i;
                return (
                  <div key={i} className="bg-white rounded-xl shadow-sm overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenFaq(open ? null : i)}
                      className="w-full flex items-center justify-between gap-3 text-left px-6 py-4 font-bold text-slate-900 hover:bg-slate-50 transition-colors"
                      aria-expanded={open}
                      data-testid={`faq-toggle-${i}`}
                    >
                      <span>{faq.q}</span>
                      <ChevronDown className={`w-5 h-5 text-amber-600 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                    </button>
                    {open && (
                      <div className="px-6 pb-5 -mt-1 text-slate-600">{faq.a}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Talk to Expert */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Talk to a Palm Bay Land Expert?</h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Whether you're buying, selling, or just exploring options — get straight answers from someone who knows Palm Bay land inside and out.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:3213337230" 
              className="px-10 py-5 bg-amber-500 text-white rounded-lg text-xl font-bold hover:bg-amber-600 transition-colors shadow-lg"
            >
              Talk to a Palm Bay Land Expert
            </a>
          </div>
          <p className="text-gray-400 mt-4">Or call directly: 321-333-7230</p>
        </div>
      </section>

      {/* Government & Map Links */}
      <section id="government-links" className="py-14 bg-slate-100 border-t border-slate-200">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2 text-center">Government &amp; Official Map Links</h2>
            <p className="text-slate-600 text-center mb-8">Verify zoning, utilities, flood, and wetlands straight from the official sources — do your due diligence with confidence.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { label: 'City of Palm Bay — Official Site', url: 'http://www.palmbayflorida.org/' },
                { label: 'Palm Bay GIS — Utility Map', url: 'https://gis.palmbayflorida.org/portal/apps/webappviewer/index.html?id=71ec29150c0b469ea3e70abeb332944c' },
                { label: 'Palm Bay GIS — Zoning Map', url: 'https://gis.palmbayflorida.org/portal/apps/webappviewer/index.html?id=c263a4dbd1834bc2a73b97f1bb88e2d5' },
                { label: 'Brevard County — Land Use Map', url: 'https://gis.palmbayflorida.org/portal/apps/webappviewer/index.html?id=c263a4dbd1834bc2a73b97f1bb88e2d5' },
                { label: 'Brevard County — Flood Map', url: 'https://www.arcgis.com/apps/webappviewer/index.html?id=6d38e10a626848b4b066e4134b8cd9ff' },
                { label: 'Brevard County — Wetlands Map', url: 'https://brevardbocc.maps.arcgis.com/apps/webappviewer/index.html?id=5af51471c684483b8d71b9c320a36495' },
                { label: 'Road Construction Plan (Year 1)', url: 'https://www.palmbayland.com/img/flyer/RoadBondPavingYear1.pdf' },
                { label: 'Emerald Lakes District Info', url: 'http://www.emeraldlakescdd.com/index.php' },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-4 py-3 hover:border-amber-400 hover:shadow-sm transition-all"
                >
                  <span className="text-sm font-medium text-slate-800">{link.label}</span>
                  <ExternalLink className="w-4 h-4 text-amber-600 shrink-0" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Social Media Share */}
      <SocialShareButtons />
    </div>
    </main>
    </>
  );
};

export default Home;
